/** 故障排查文章：每篇正文只放 2 条不重复的内链 */
export const TROUBLESHOOTING_POSTS = {
	'gb-whatsapp-verification-code-not-received': {
		slug: 'gb-whatsapp-verification-code-not-received',
		categoryId: 'troubleshooting',
		title: 'Why Is GB WhatsApp Not Sending a Verification Code?',
		description:
			'GB WhatsApp Pro verification code not arriving? Check the number, which SIM gets SMS, and whether the screen is actually asking for the official app.',
		keywords: 'gb whatsapp verification code not received',
		h1: 'GB WhatsApp Verification Code Not Received',
		lead:
			'If the code never shows up, fix the number and the SIM before you request it again. A screen that tells you to use official WhatsApp is a different problem from a late text.',
		body: `
      <h2>What is happening</h2>
      <p>GB WhatsApp Pro asks WhatsApp for a code by SMS or by a voice call. The text can be late, sent to the other SIM, or blocked after too many tries. Sometimes the app never requests a code at all because the number is already limited.</p>
      <p>Look at the screen. A countdown that says “try again in” is a wait. A full-page notice that you must continue in official WhatsApp is not an SMS delay. For that banner, use <a href="/gb-whatsapp-says-use-official-whatsapp/">what to do when GB WhatsApp tells you to switch to the official app</a> instead of tapping Resend.</p>

      <h2>Check these before you resend</h2>
      <ol class="article-steps">
        <li><strong>Read the number back.</strong> Country code first, then the mobile number without a trunk zero your country does not use internationally.</li>
        <li><strong>See which SIM can get texts.</strong> On a dual-SIM phone, set the registered SIM as the default for SMS, or turn the other SIM off for one try.</li>
        <li><strong>Wait out the timer.</strong> Asking for ten codes in a few minutes usually makes the next wait longer.</li>
        <li><strong>Try the call option if it is offered.</strong> A voice code still has to reach the SIM you typed in.</li>
      </ol>
      <p>Airplane mode, a full inbox, and SMS-blocker apps can drop the message even when the number is right. Send yourself a normal text from another phone. If that also fails, the carrier is the problem, not the APK.</p>

      <h2>If the text arrives and setup still stops</h2>
      <p>Enter the code once. Do not refresh the registration screen while you type, or the code can expire. If Android then says the app was not installed, or the wizard closes before chats appear, the SMS part is finished. Continue with <a href="/installation/android/">installing GB WhatsApp Pro on Android after the code arrives</a>.</p>
      <p>Never send the code to someone who messages you and claims to be support. WhatsApp does not ask for it in a chat.</p>
    `,
		faqs: [
			['Should I keep tapping Resend?', 'No. Wait for the timer. Rapid retries are a common reason the next code takes much longer.'],
		],
		publishedAt: '2026-10-18',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Verification',
	},
	'gb-whatsapp-keeps-crashing': {
		slug: 'gb-whatsapp-keeps-crashing',
		categoryId: 'troubleshooting',
		title: 'GB WhatsApp Keeps Crashing? Causes and Fixes',
		description:
			'GB WhatsApp Pro closes on open, on one chat, or right after an update. Try the checks that do not delete chats first.',
		keywords: 'gb whatsapp keeps crashing, gb whatsapp crash fix',
		h1: 'GB WhatsApp Keeps Crashing',
		lead:
			'A crash usually has a local cause: low storage, one bad chat, or a build that your Android version cannot run. Start with checks that do not wipe chats.',
		body: `
      <h2>Start with checks that cannot lose data</h2>
      <p>Do these before you clear storage or uninstall. Stop as soon as the app stays open.</p>
      <ol class="article-steps">
        <li><strong>Restart the phone.</strong> A full restart clears a stuck process. If the crash stops, you are done.</li>
        <li><strong>Free storage.</strong> Keep at least 1 GB free in Android Settings → Storage. A nearly full phone is a common reason GB WhatsApp Pro closes while opening media.</li>
        <li><strong>Note when it closes.</strong> On startup, on one chat, after an update, or at random. The pattern picks the next step.</li>
      </ol>

      <h2>Match the fix to the crash</h2>
      <div class="article-table-wrap">
        <table class="article-table">
          <thead>
            <tr><th>When it closes</th><th>Try this first</th></tr>
          </thead>
          <tbody>
            <tr><td>Right after an update</td><td>Undo the last theme or font change, then reinstall the build that worked</td></tr>
            <tr><td>Only one chat</td><td>Back up, then remove recent large files from that chat’s media</td></tr>
            <tr><td>Every time you tap the icon</td><td>Remove a second WhatsApp or GB install, then install one build</td></tr>
            <tr><td>Randomly, phone feels full</td><td>Free space and close other apps</td></tr>
          </tbody>
        </table>
      </div>
      <p>If the new APK installed and the crash started the moment you opened it, you are in the same situation as <a href="/gb-whatsapp-not-working-after-update/">GB WhatsApp Pro that installs but will not stay open after an update</a>. Roll back to the last build that ran, instead of clearing data on the broken one.</p>

      <h2>Before you clear data</h2>
      <p>Clearing data or uninstalling removes local chats that were never backed up. Do it only after a backup you can see inside the app, and only if the steps above failed. Confirm that backup finished with <a href="/installation/backup-chats/">a local GB WhatsApp chat backup you can actually find</a> before you remove the app.</p>
    `,
		faqs: [
			['Should I clear data first?', 'No. Restart and free storage first. Clearing data can delete chats that were only stored on the phone.'],
		],
		publishedAt: '2026-10-19',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'App Crashes',
	},
	'gb-whatsapp-installation-error': {
		slug: 'gb-whatsapp-installation-error',
		categoryId: 'troubleshooting',
		title: 'GB WhatsApp Installation Error? How to Troubleshoot',
		description:
			'App not installed, parse error, or a package conflict when installing GB WhatsApp Pro. Match the Android message before you delete the app you already use.',
		keywords: 'gb whatsapp installation error, app not installed',
		h1: 'GB WhatsApp Installation Error',
		lead:
			'Read the exact Android message. “App not installed”, “problem parsing the package”, and a conflict with another WhatsApp are three different fixes.',
		body: `
      <h2>Match the message</h2>
      <div class="article-table-wrap">
        <table class="article-table">
          <thead>
            <tr><th>Android says</th><th>Usual cause</th><th>What to do</th></tr>
          </thead>
          <tbody>
            <tr><td>App not installed</td><td>Blocked unknown apps, low space, or a build this phone cannot run</td><td>Allow installs for the app that opened the file, free space, then check the Android version</td></tr>
            <tr><td>Problem parsing the package</td><td>The download stopped early</td><td>Delete the file and download it again. Do not rename the extension</td></tr>
            <tr><td>App not installed because a conflicting package exists</td><td>Another GB or WhatsApp copy uses a clashing signature</td><td>Back up first, then remove only the copy you no longer need</td></tr>
          </tbody>
        </table>
      </div>
      <p>On Samsung and Xiaomi phones, “install unknown apps” is per app. If you opened the APK from Files, Chrome’s permission does not count. Turn it on for the app that showed the installer.</p>

      <h2>When the phone itself cannot run the file</h2>
      <p>A parse error on a complete download is often the wrong Android version or a 32-bit package on a 64-bit-only phone. Check Settings → About phone before you try a third mirror. <a href="/gb-whatsapp-android-compatibility/">Whether this Android version can run GB WhatsApp Pro</a> is the check to do before you uninstall the app that already works.</p>

      <h2>Before you remove the old copy</h2>
      <p>Uninstalling to “make room” for a new signature deletes local chats unless a backup exists. If you still need those chats, finish <a href="/installation/backup-chats/">saving chats from the GB WhatsApp you already have installed</a> while that copy still opens. Then remove the conflicting package and install once.</p>
    `,
		faqs: [
			['Why does Android block the APK?', 'Unknown-app installs are off until you allow them for one app. Allow only the file manager or browser you trust.'],
		],
		publishedAt: '2026-10-20',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Install Error',
	},
	'gb-whatsapp-messages-not-sending': {
		slug: 'gb-whatsapp-messages-not-sending',
		categoryId: 'troubleshooting',
		title: 'GB WhatsApp Messages Not Sending? What to Check',
		description:
			'GB WhatsApp Pro messages stuck on the clock or one tick. Separate a bad connection, one blocked chat, and a problem that started after an update.',
		keywords: 'gb whatsapp messages not sending, pending message',
		h1: 'GB WhatsApp Messages Not Sending',
		lead:
			'A clock icon means the message has not left the phone. Check whether every chat is stuck, or only one person, before you change settings.',
		body: `
      <h2>See how far the message got</h2>
      <p>A clock means it is still on your phone. One grey tick means it reached WhatsApp’s servers. Two grey ticks mean it reached their phone. Two blue ticks mean they opened the chat. A message that never leaves the clock is a connection, date, or account limit. A message that gets one tick and stops may be their phone, a block, or a limit on new chats.</p>
      <p>Send a short text to two different people. If both stay on the clock, the problem is your phone or your account. If only one person fails, they may have blocked you or their phone is off. Do not clear app data to test that.</p>

      <h2>Fix the phone first</h2>
      <ol class="article-steps">
        <li><strong>Turn airplane mode on for 10 seconds, then off.</strong> Use mobile data once without a VPN.</li>
        <li><strong>Set date and time to automatic.</strong> A wrong clock can stop the connection even when the internet works in Chrome.</li>
        <li><strong>Allow unrestricted battery use</strong> for GB WhatsApp Pro if the phone kills background apps.</li>
      </ol>
      <p>If texts send and only videos sit on the clock, you are short on space or the file is too large for the current network. Free storage, then try a small photo on Wi‑Fi. The same stall is covered in <a href="/help/storage-problems/">when GB WhatsApp media will not send because storage is full</a>.</p>

      <h2>If it started right after an update</h2>
      <p>A build that opens but will not send is not the same as an APK that failed to install. Note whether the clock started the day you updated. That path is <a href="/gb-whatsapp-not-working-after-update/">messages that stop sending right after a GB WhatsApp update</a>. Restart once before you reinstall, and do not clear data until you know a backup exists.</p>
    `,
		faqs: [
			['Does battery saver stop sends?', 'It can. If messages leave only while the app is on screen, allow background data and unrestricted battery for this app.'],
		],
		publishedAt: '2026-10-21',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Messaging',
	},
	'gb-whatsapp-status-download-not-working': {
		slug: 'gb-whatsapp-status-download-not-working',
		categoryId: 'troubleshooting',
		title: 'Why Can’t I Download GB WhatsApp Status Videos?',
		description:
			'GB WhatsApp Pro will not save a status photo or video. Check permissions, free space, and whether your build still has a save button.',
		keywords: 'gb whatsapp status download not working',
		h1: 'GB WhatsApp Status Download Not Working',
		lead:
			'If the save button does nothing, check storage permission and free space first. If there is no save button at all, this build may not include status saving.',
		body: `
      <h2>Button missing or button does nothing</h2>
      <p>Open a status and let it play to the end. A download or save icon usually sits near the reply field. No icon means the feature is off or not in this version. An icon that taps with no new file means Android blocked storage or the disk is full.</p>
      <p>Statuses disappear after 24 hours. If the ring is already gone, there is nothing left to save. Open it again from the contact before you look for a file.</p>

      <h2>Permissions and space</h2>
      <ol class="article-steps">
        <li><strong>Allow photos and videos</strong> for GB WhatsApp Pro. On Android 13 and newer, photo and video are separate switches. Turn both on.</li>
        <li><strong>Free at least a few hundred megabytes.</strong> A long video fails quietly when internal storage is full.</li>
        <li><strong>Watch the status once, then tap save.</strong> Some builds hide the button until the first view finishes.</li>
      </ol>
      <p>After a save that seems to work, search the file manager by today’s date. The folder name changes by build. It is often under Android/media, not the main Gallery album. If photos in chats fail the same way, the permission problem is wider than status. Use <a href="/help/photos-videos-not-downloading/">when GB WhatsApp photos and videos will not save at all</a>.</p>

      <h2>When the feature is simply not there</h2>
      <p>Updating does not always keep status download. If the icon never appears on a current build, you are not looking at a broken permission. <a href="/download-gb-whatsapp-status-videos/">How status saving works when GB WhatsApp Pro still includes it</a> shows the normal button and the folder to expect. Do not install a random “status downloader” APK to replace a missing icon.</p>
    `,
		faqs: [
			['Can I save a status after it expires?', 'No. Once the 24-hour status is gone from the server, the save button has nothing to store.'],
		],
		publishedAt: '2026-10-22',
		updatedAt: '2026-11-04',
		coverTheme: 'install',
		coverLabel: 'Status Save',
	},
}
